import { useState } from 'react';

import { DateTime } from 'luxon';

import { DatePicker } from 'src/common/date/DatePicker';
import { usePayersListQuery } from 'src/common/external/bambi-api/bambiApi';
import { useLazyGetInvoicesExportListAsCsvQuery } from 'src/common/external/bambi-api/emptyApi';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import { Button } from 'src/common/primitives/Button';
import { Modal, ModalProps } from 'src/common/primitives/Modal';
import { Select, SelectOption } from 'src/common/primitives/Select';
import { datePickerInput } from 'src/features/trip/management/datePickerInput';

interface DownloadInvoicesProps {
  open: ModalProps['open'];
  setOpen: ModalProps['setOpen'];
  onClose: ModalProps['onClose'];
}

export function DownloadInvoices({
  open,
  setOpen,
  onClose,
}: DownloadInvoicesProps) {
  const [startDate, setStartDate] = useState<DateTime | undefined>(undefined);
  const [endDate, setEndDate] = useState<DateTime | undefined>(undefined);
  const [payer, setPayer] = useState<SelectOption[] | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [trigger] = useLazyGetInvoicesExportListAsCsvQuery();

  const { data: payers } = usePayersListQuery({}, { skip: !open });

  const handleSubmit = async () => {
    if (!payer) {
      setError('Please select a Payer');
      return;
    }
    if (!startDate) {
      setError('Please select a start range');
      return;
    }
    setLoading(true);

    try {
      setError(undefined);

      await trigger({
        rangeStart: startDate?.startOf('day').toISO(),
        rangeEnd: endDate?.endOf('day').toISO(),
        tripPayer: payer?.[0].value,
        format: 'csv',
      }).unwrap();

      onClose?.();
    } catch (error) {
      const errMsg = 'There was an error downloading the data.';
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const payerOptions = payers?.results?.map((payer) => ({
    label: payer.payer_name ?? '',
    value: payer.id,
  }));

  return (
    <Modal open={open} setOpen={setOpen} contentClassnames="max-w-6xl">
      <h1 className="text-2xl">Select details for this list to download</h1>
      {error && <FormErrorMessage>{error}</FormErrorMessage>}

      <div className="mt-2">
        <div>Select the time range *</div>
        <div className="flex items-center justify-between gap-2 pt-2">
          <DatePicker
            triggerElement={datePickerInput('Start Date', startDate)}
            onChange={setStartDate}
            value={startDate || DateTime.now()}
          />
          ~
          <DatePicker
            triggerElement={datePickerInput('End Date', endDate)}
            onChange={setEndDate}
            value={endDate || DateTime.now()}
          />
        </div>
      </div>
      <div className="py-4">
        <div>Payer *</div>
        <Select
          selected={payer?.length ? payer[0] : undefined}
          placeholder="Select a payer"
          options={payerOptions ?? []}
          onChange={(selected) => {
            if (selected) setPayer([selected]);
          }}
        />
      </div>
      <div className="grid grid-cols-2 gap-4 pt-6">
        <Button loading={loading} onClick={() => onClose?.()}>
          Cancel
        </Button>
        <Button
          dataTestId="download-csv"
          variant="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Export as CSV
        </Button>
      </div>
    </Modal>
  );
}
